import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action } from '@ember/object';
import Store from '@ember-data/store';
import JournalService from 'donor-elf-web/services/journal-service';
import FundModel from 'donor-elf-web/models/fund';
import FundsService from 'donor-elf-web/services/funds-service';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import JournalSummaryDetailModel from 'donor-elf-web/models/journal-summary-detail';
import JournalGoalModel from 'donor-elf-web/models/journal-goal';
import JournalModel from 'donor-elf-web/models/journal';
import {
  CalculateIntervalCumulativeDecisionSummaries,
  type JournalIntervalCumulativeDecisionSummary,
  JournalIntervalDate,
} from 'donor-elf-web/lib/journal-utils';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import { htmlSafe } from '@ember/template';

interface SummaryViewItem {
  fund: FundModel;
  journal: JournalModel;
  journalGoal: JournalGoalModel;
  intervalSummaries: JournalIntervalCumulativeDecisionSummary[];
}

export default class JournalsSummariesViewController extends Controller {
  queryParams = ['ids'];

  @service declare fundsService: FundsService;
  @service declare formatDateService: FormatDateService;
  @service declare store: Store;
  @service declare journalService: JournalService;

  @tracked ids?: string;
  @tracked loading: boolean = false;
  @tracked items: SummaryViewItem[] = [];

  get journalIds() {
    return this.ids?.split('-') || [];
  }

  get percentDownloaded() {
    let { journalIds, items } = this;

    if (journalIds.length === 0) {
      return 0;
    }
    return Math.max(Math.round((items.length * 100) / journalIds.length), 5);
  }

  get progressBarStyle() {
    return htmlSafe(`width: ${this.percentDownloaded}%`);
  }

  @action
  async templateDidInsert() {
    this.loading = true;
    this.items = [];

    let allFundsInfo = await this.fundsService.getAllFundsInfo();

    await queryWithPages<JournalSummaryDetailModel>({
      store: this.store,
      modelName: 'journal-summary-detail',
      queryOptions: {
        filter: {
          ids: this.journalIds,
        },
      },
      pageSize: 5,
      pageCallback: (args) => {
        let pageItems: SummaryViewItem[] = [];
        args.pageRecords.forEach((journalSummaryDetail) => {
          let fund = allFundsInfo.fundsById[journalSummaryDetail.fundId];
          let journalGoal = journalSummaryDetail.journalGoals.toArray()[0];
          let journalIntervalDates = journalSummaryDetail.intervalDates
            .slice()
            .map(
              (x) =>
                new JournalIntervalDate({
                  start: x.start,
                  end: x.end,
                  formatDateService: this.formatDateService,
                }),
            );
          let yesDecisions = journalSummaryDetail.decisions
            .filter((x) => x.yesNo && x.journalGoalId == journalGoal.id)
            .map((x) => {
              return {
                decisionDate: x.decisionDateString,
                amount: x.amount!,
                firstGiftDate: x.firstGiftDateString,
              };
            });
          let intervalSummaries = CalculateIntervalCumulativeDecisionSummaries(
            journalIntervalDates,
            yesDecisions,
          );
          pageItems.push({
            fund,
            journal: journalSummaryDetail.journal,
            journalGoal,
            intervalSummaries,
          });
        });
        this.items = [...this.items, ...pageItems];
      },
    });

    this.loading = false;
  }
}
